<template>
  <div>
    <h1>Data Imports</h1>
    <p class="text-muted font-weight-bold">
      Manage data integrations and import operations
    </p>
    <hr />

    <!-- Action Cards -->
    <b-row>
      <b-col md="4" v-for="(action, index) in actions" :key="index">
        <b-card no-body class="cursor-pointer shadow-sm hover-card bg-white">
          <b-card-body class="p-2">
            <div class="d-flex align-items-center">
              <div
                class="avatar-content rounded d-flex align-items-center justify-content-center mr-2"
                :class="`bg-${action.variant}`"
                style="width: 48px; height: 48px"
              >
                <feather-icon :icon="action.icon" size="24" />
              </div>
              <div>
                <h5 class="font-weight-bold text-dark mb-0">
                  {{ action.title }}
                </h5>
                <small>{{ action.desc }}</small>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Main Content -->
    <b-row>
      <!-- Connected Sources -->
      <b-col md="4">
        <b-card no-body>
          <b-card-header>
            <b-card-title>Connected Sources</b-card-title>
          </b-card-header>
          <b-card-body>
            <div
              v-for="(source, index) in connectedSources"
              :key="index"
              class="border rounded p-2 mb-2 d-flex justify-content-between align-items-center shadow-sm hover-items"
            >
              <div class="d-flex align-items-start">
                <div class="mt-50 mr-1">
                  <div
                    :class="`bg-${source.status}`"
                    class="rounded-circle"
                    style="width: 8px; height: 8px"
                  ></div>
                </div>
                <div>
                  <h6 class="font-weight-bold mb-0 text-dark">
                    {{ source.name }}
                  </h6>
                  <small>{{ source.type }} • {{ source.lastSync }}</small>
                </div>
              </div>
              <b-button variant="flat-secondary" class="btn-icon">
                <feather-icon icon="SettingsIcon" size="16" />
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Scheduled Jobs -->
      <b-col md="8">
        <b-card no-body>
          <b-card-header>
            <b-card-title>Scheduled Jobs</b-card-title>
          </b-card-header>
          <b-card-body>
            <div
              v-for="(job, index) in scheduledJobs"
              :key="index"
              class="border rounded p-2 mb-2 d-flex justify-content-between align-items-center shadow-sm hover-items"
            >
              <div class="d-flex align-items-center">
                <div class="mr-2">
                  <feather-icon icon="ClockIcon" size="20" />
                </div>

                <div>
                  <h6 class="font-weight-bold mb-0 text-dark">
                    {{ job.name }}
                  </h6>
                  <small>{{ job.schedule }}</small>
                </div>
              </div>

              <div class="d-flex align-items-center">
                <small class="mr-2 d-none d-md-block text-dark">{{
                  job.nextRun
                }}</small>
                <b-badge
                  pill
                  variant="dark"
                  style="background-color: #1e1e2d"
                  class="px-2"
                >
                  {{ job.status }}
                </b-badge>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Recent Imports Table -->
    <b-card no-body class="mt-2">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <b-card-title>Recent Imports</b-card-title>
          <b-button variant="outline-secondary" size="sm">
            <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
            Export Log
          </b-button>
        </div>
      </b-card-header>
      <b-card-body class="p-0">
        <div class="table-responsive">
          <table class="table b-table table-hover mb-0">
            <thead class="bg-light">
              <tr>
                <th class="pl-2">Import Name</th>
                <th>Source</th>
                <th>Records</th>
                <th>Duration</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in recentImports" :key="index">
                <td class="pl-2 font-weight-bold">
                  <feather-icon
                    icon="FileTextIcon"
                    size="16"
                    class="mr-50 text-secondary"
                  />
                  {{ item.name }}
                </td>
                <td>{{ item.source }}</td>
                <td>{{ item.records }}</td>
                <td>{{ item.duration }}</td>
                <td>{{ item.date }}</td>
                <td>
                  <b-badge
                    pill
                    :variant="item.status === 'completed' ? 'dark' : 'danger'"
                    :style="
                      item.status === 'completed'
                        ? 'background-color: #1e1e2d'
                        : ''
                    "
                  >
                    <feather-icon
                      :icon="
                        item.status === 'completed'
                          ? 'CheckCircleIcon'
                          : 'AlertCircleIcon'
                      "
                      size="12"
                      class="mr-25"
                    />
                    {{ item.status }}
                  </b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BButton,
} from "bootstrap-vue";

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BButton,
  },
  data() {
    return {
      actions: [
        {
          title: "Upload CSV",
          desc: "Import data from file",
          icon: "UploadIcon",
          variant: "light",
        },
        {
          title: "Connect Source",
          desc: "Add new integration",
          icon: "DatabaseIcon",
          variant: "light-info",
        },
        {
          title: "Using API",
          desc: "Trigger sync now",
          icon: "RefreshCwIcon",
          variant: "light-success", // Using light-success for green tint
        },
      ],
      connectedSources: [
        {
          name: "PowerSchool SIS",
          type: "API",
          lastSync: "2 hours ago",
          status: "success",
        },
        {
          name: "Renaissance Star",
          type: "SFTP",
          lastSync: "1 day ago",
          status: "success",
        },
        {
          name: "NWEA MAP",
          type: "API",
          lastSync: "3 days ago",
          status: "success",
        },
        {
          name: "Infinite Campus",
          type: "API",
          lastSync: "Never",
          status: "danger",
        },
      ],
      scheduledJobs: [
        {
          name: "PowerSchool Sync",
          schedule: "Daily at 6:00 AM",
          nextRun: "Next: Tomorrow, 6:00 AM",
          status: "active",
        },
        {
          name: "Assessment Data Pull",
          schedule: "Weekly on Mondays",
          nextRun: "Next: Feb 3, 2025",
          status: "active",
        },
        {
          name: "Attendance Sync",
          schedule: "Daily at 11:00 PM",
          nextRun: "Next: Today, 11:00 PM",
          status: "active",
        },
      ],
      recentImports: [
        {
          name: "Student Demographics Q2",
          source: "PowerSchool",
          records: "4,892",
          duration: "2m 34s",
          date: "Jan 28, 2025",
          status: "completed",
        },
        {
          name: "Assessment Scores - Winter",
          source: "CSV Upload",
          records: "12,450",
          duration: "5m 12s",
          date: "Jan 27, 2025",
          status: "completed",
        },
        {
          name: "Teacher Roster Update",
          source: "PowerSchool",
          records: "248",
          duration: "45s",
          date: "Jan 25, 2025",
          status: "completed",
        },
        {
          name: "Standards Alignment",
          source: "Manual Entry",
          records: "156",
          duration: "1m 08s",
          date: "Jan 20, 2025",
          status: "completed",
        },
        {
          name: "Intervention Records",
          source: "CSV Upload",
          records: "342",
          duration: "1m 22s",
          date: "Jan 18, 2025",
          status: "failed",
        },
      ],
    };
  },
};
</script>

<style scoped>
.hover-card {
  transition: all 0.2s ease-in-out;
}
.hover-card:hover {
  transform: translateY(-2px);
}
.bg-light-success {
  background-color: #dcfce7 !important;
  color: #28c76f;
}
.bg-light-info {
  background-color: #dbeafe !important;
  color: #00cfe8;
}

.hover-items {
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
}
.hover-items:hover {
  transform: translateY(-2px);
}
</style>
